import {Component, OnInit, ViewChild} from '@angular/core';
import {SnackBarService} from '../../services/common/snack-bar/snack-bar.service';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {LogicalConjunctionService} from '../../services/common/logical-conjunction/logical-conjunction.service';
import {MatDialog} from '@angular/material/dialog';
import {NodeApiService} from '../../services/api/network/online/node/node-api.service';
import {ServerPageRequest} from '../../classes/common/table/server-page-request';
import {PageEvent} from '@angular/material/paginator';
import {DisplayedColumnInf} from '../../interfaces/common/table/displayed-column-inf';
import {TableColumnsConfigurationDialogComponent} from '../../dialogs/table-columns-configuration-dialog/table-columns-configuration-dialog.component';
import {SearchTermDefinitionInf} from '../../interfaces/common/search/search-term-definition-inf';
import {NodesSearchComponent} from './nodes-search/nodes-search.component';
import {AdminStatePipe} from '../../pipes/admin-state/admin-state.pipe';
import {OperativeStatePipe} from '../../pipes/operative-state/operative-state.pipe';
import {
  RoutingInterfaceApiService
} from '../../services/api/network/online/routing-interface/routing-interface-api.service';
import {ArmTableComponent} from '../../components/arm-table/arm-table.component';
import {LoaderService} from '../../services/common/loader-service/loader.service';
import Search from '../../utils/search';
import {NodesDialogService} from './nodes-dialog/nodes-dialog.service';

@Component({
  selector: 'nodes',
  templateUrl: './nodes.component.html',
  styleUrls: ['./nodes.component.scss']
})
export class NodesComponent implements OnInit {

  @ViewChild(ArmTableComponent) armTable;

  selection;
  isUnauthorized: boolean;
  items = [];
  search: any = {};
  isMultipleSelection = true;
  data = {elements: [], totalElements: 0};
  length;

  serverPageRequest: ServerPageRequest = new ServerPageRequest({
    pageIndex: 0,
    pageSize: 25,
    length: 0
  });

  displayedColumns: Array<DisplayedColumnInf> = [
    {name: 'name', display: 'Name', selected: true, width: 200},
    {name: 'adminState', display: 'Admin State', selected: true, width: 200, transformer: AdminStatePipe},
    {name: 'operState', path: 'operState.description', display: 'Operative state',
      selected: true, width: 200, transformer: OperativeStatePipe},
    {name: 'addressForGUI', display: 'Address', selected: true, width: 200},
    {name: 'serialNum', display: 'Serial', selected: true, width: 200},
    {name: 'prevSerialNum', display: 'Secondary serial', selected: true, width: 200},
    {name: 'softwareVersion', display: 'Software version', selected: true, width: 200},
    {name: 'productType', display: 'Product type', selected: true, width: 200}
  ];

  searchData = {};
  nodesSearchComponent = NodesSearchComponent;
  operStateOptions = [
    {text: 'Available', value: 'AVAILABLE'},
    {text: 'Unavailable', value: 'UNAVAILABLE'},
    {text: 'Invalid Configuration', value: 'INVALID_CONFIGURATION'},
    {text: 'Login', value: 'LOGIN'},
    {text: 'Logout', value: 'LOGOUT'},
    {text: 'Unknown', value: 'UNKNOWN'},
    {text: 'Unroutable', value: 'UNROUTABLE'}
  ];

  adminStateOptions = [
    {text: 'Locked', value: 'LOCKED'},
    {text: 'Unlocked', value: 'UNLOCKED'}
  ];


  searchTerms: SearchTermDefinitionInf[] = [
    {term: 'name', operator: ':', displayName: 'Name'},
    {term: 'ipAddr', operator: '=', displayName: 'IP Address'},
    {term: 'serial', operator: '=', displayName: 'Serial'},
    {term: 'hostname', operator: '=', displayName: 'Hostname'},
    {term: 'operState', operator: '=', displayName: 'Operative State', displayValue: Search.getOptionValue(this.operStateOptions)},
    {term: 'adminState', operator: '=', displayName: 'Administrative State', displayValue: Search.getOptionValue(this.adminStateOptions)}
  ];

  constructor(public dialog: MatDialog,
              private nodeApiService: NodeApiService,
              private snackBarService: SnackBarService,
              private userPrivilege: UserPrivilegeService,
              private logicalConjunctionService: LogicalConjunctionService,
              private routingInterfaceApiService: RoutingInterfaceApiService,
              private nodesDialogService: NodesDialogService,
              private loaderService: LoaderService) {
  }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized();

    this.refresh();
  }

  onSelection(selection: any): void {
    this.selection = selection;
  }

  refresh(): void {
    this.refreshItems();
    this.setSearchOptions();
  }

  editItem(): void{
    this.nodesDialogService.edit(this.selection[0], () => {
      this.refresh();
    });
  }

  removeItem(): void {
    this.nodesDialogService.delete(this.selection[0], () => {
      this.refresh();
    });
  }

  configureNode(): void {
    this.nodeApiService.configureNode(this.selection[0].id).then((value: any) => {
      window.open(value.url);
    }).catch((error) => {
      window.open(`${this.selection[0].transportType.toLowerCase()}://${this.selection[0].ipAddr}`);
      console.log(`Exception was thrown in configure node ${this.selection[0].name}, reason: ${error}`);
    });
  }

  openColumnsConfiguration(): void {
    const dialogRef = this.dialog.open(TableColumnsConfigurationDialogComponent, {
      width: '350px',
      data: {
        columns: this.displayedColumns
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      dialogRef.close();
    });
  }

  isMixedAdminState(): boolean {
    if (!this.selection || this.selection.length < 1){
      return true;
    }
    const firstSelectedNode = this.selection[0];

    return this.selection.some(node => firstSelectedNode.adminState !== node.adminState);
  }

  lock(): void {
    this.nodeApiService.lockNodes(this.selection.map(selectionItem => selectionItem.id)).then(() => {
      this.refresh();
      this.snackBarService.display(`${this.logicalConjunctionService
        .generateAndDescription(this.selection, 'name')} successfully locked`);
    }).catch((error) => {
      console.log(`Exception was thrown in lock node ${this.selection[0].name}, reason: ${error}`);
    });
  }

  unlock(): void {
    this.nodeApiService.unlockNodes(this.selection.map(selectionItem => selectionItem.id)).then(() => {
      this.refresh();
      this.snackBarService.display(`${this.logicalConjunctionService
        .generateAndDescription(this.selection, 'name')} successfully unlocked`);
    }).catch((error) => {
      console.log(`Exception was thrown in unlock node ${this.selection[0].name}, reason: ${error}`);
    });
  }

  syncNode(): void {
    this.nodeApiService.syncNode(this.selection[0].id).then(() => {
      this.snackBarService.display(`sync "${this.selection[0].name}" started`);
    }).catch((error) => {
      console.log(`Exception was thrown in sync node ${this.selection[0].name}, reason: ${error}`);
    });
  }

  adminStateTooltip(): string {
    return this.isAdminStateLocked() ? 'Unlock' : 'Lock';
  }

  adminStateIcon(): string {
    return this.isAdminStateLocked() ? 'fa-unlock' : 'fa-lock';
  }

  pageChanged(pageInfo: PageEvent): void {
    this.serverPageRequest = new ServerPageRequest(pageInfo);

    this.refreshItems();
  }

  isSingleSelection(): boolean {
    return this.selection && this.selection.length === 1;
  }

  isAnySelection(): boolean {
    return this.selection && this.selection.length > 0;
  }

  onSearch(data): void {
    this.armTable.firstPage();
    this.serverPageRequest.firstPage();
    this.search = data;
    this.refreshItems();
  }

  refreshItems(): void {
    const params = {...this.search, ...this.serverPageRequest};
    this.loaderService.showMainContainer();
    this.nodeApiService.getNodes(params).then((data) => {
      this.data = data;
      this.length  = data.totalElements;
      this.loaderService.hideMainContainer();
    }).catch((error) => {
      this.loaderService.hideMainContainer();
      console.log(`Exception was thrown in refresh nodes, reason: ${error}`);
    });
  }

  public isAdminStateLocked(): boolean {
    return this.selection &&
      this.selection[0] &&
      this.selection[0].adminState === 'LOCKED';
  }

  private setSearchOptions(): void {
    this.searchData = {
      operStateOptions: this.operStateOptions,
      adminStateSearchOptions: this.adminStateOptions
    };
  }

  isThirdPartyNode(): boolean {
    return this.selection && this.selection[0] && this.selection[0].productType === 'THIRD_PARTY';
  }

  saveIniFile(): void {
    this.nodeApiService.getNodeINIFile(this.selection[0].id);
  }
}
